import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import i18n from '../i18n';

type AuthState = { token: string | null; role: string | null; method: string | null };

// isAuthenticated を追加（将来 Navbar 等でこれを直接使うと安全）
type Ctx = {
  auth: AuthState;
  setAuth: (s: AuthState) => void;
  login: (token: string, role: string, method: string) => void;
  logout: () => void;
  isAuthenticated: boolean;
};

const AuthContext = createContext<Ctx | null>(null);

// ----- 文字列を正規化：'null' / 'undefined' / 空文字 は null 扱い -----
const normalize = (v: string | null): string | null => {
  if (v == null) return null;
  const s = String(v).trim();
  if (s === '' || s.toLowerCase() === 'null' || s.toLowerCase() === 'undefined') return null;
  return s;
};

// ----- JWT の有効期限チェック -----
const isTokenExpired = (token: string | null): boolean => {
  if (!token) return true;
  try {
    // JWT は Base64url（パディングなし）なので atob 前にパディングを補完する
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
    const payload = JSON.parse(atob(b64 + pad));
    return payload.exp < Math.floor(Date.now() / 1000);
  } catch {
    return true;
  }
};

// ----- localStorage から正規化して読み出す（期限切れは null を返すだけ・削除は useEffect に委ねる） -----
const readAuthFromStorage = (): AuthState => {
  const token = normalize(localStorage.getItem('token'));
  if (isTokenExpired(token)) {
    return { token: null, role: null, method: null };
  }
  return {
    token,
    role: normalize(localStorage.getItem('role')),
    method: normalize(localStorage.getItem('login_method')),
  };
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // 初期読み出し時点で正規化
  const [auth, setAuth] = useState<AuthState>(readAuthFromStorage());

  // ----- 起動時クリーンアップ：不正値 + 期限切れトークンを削除 -----
  useEffect(() => {
    const tokRaw = localStorage.getItem('token');
    const tok = normalize(tokRaw);

    // 期限切れの場合はすべてクリア
    if (isTokenExpired(tok)) {
      localStorage.removeItem('token');
      localStorage.removeItem('role');
      localStorage.removeItem('login_method');
      setAuth({ token: null, role: null, method: null });
      return;
    }

    const roleRaw = localStorage.getItem('role');
    const methodRaw = localStorage.getItem('login_method');
    const role = normalize(roleRaw);
    const method = normalize(methodRaw);
    if (tok !== tokRaw) {
      if (tok === null) localStorage.removeItem('token');
      else localStorage.setItem('token', tok);
    }
    if (role !== roleRaw) {
      if (role === null) localStorage.removeItem('role');
      else localStorage.setItem('role', role);
    }
    if (method !== methodRaw) {
      if (method === null) localStorage.removeItem('login_method');
      else localStorage.setItem('login_method', method);
    }
    setAuth({ token: tok, role, method });
  }, []);

  // ----- localStorage の setItem/removeItem をラップして token/role 変化を即時反映 -----
  useEffect(() => {
    const rawSetItem = localStorage.setItem;
    const rawRemoveItem = localStorage.removeItem;

    // setItem をパッチ：'null' 等を渡されたら remove に置換
    localStorage.setItem = function (key: string, value: string) {
      if (key === 'token' || key === 'role' || key === 'login_method') {
        const norm = normalize(value);
        if (norm === null) {
          rawRemoveItem.call(localStorage, key);
        } else {
          rawSetItem.call(localStorage, key, norm);
        }
        const next = readAuthFromStorage();
        setAuth(next);
        window.dispatchEvent(new Event('auth:changed'));
        return; // 既に処理済み
      }
      rawSetItem.call(localStorage, key, value);
    } as typeof localStorage.setItem;

    localStorage.removeItem = function (key: string) {
      rawRemoveItem.call(localStorage, key);
      if (key === 'token' || key === 'role' || key === 'login_method') {
        const next = readAuthFromStorage();
        setAuth(next);
        window.dispatchEvent(new Event('auth:changed'));
      }
    } as typeof localStorage.removeItem;

    return () => {
      // 元のメソッドに戻す
      localStorage.setItem = rawSetItem;
      localStorage.removeItem = rawRemoveItem;
    };
  }, []);

  // ----- 他タブの変更やカスタムイベントで同期 -----
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      // token/role/login_method の変更、或者全消去（key=null）を捕捉
      if (e.key === 'token' || e.key === 'role' || e.key === 'login_method' || e.key === null) {
        setAuth(readAuthFromStorage());
      }
    };
    const onCustom = () => setAuth(readAuthFromStorage());

    window.addEventListener('storage', onStorage);
    window.addEventListener('auth:changed', onCustom as EventListener);

    // フォーカス復帰時にも同期（ログイン画面などでズレないように）
    const onFocus = () => setAuth(readAuthFromStorage());
    const onVisibility = () => document.visibilityState === 'visible' && setAuth(readAuthFromStorage());
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('auth:changed', onCustom as EventListener);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // ----- ログイン：正規化して保存（不正値は remove 扱い） -----
  const login = (token: string, role: string, method: string) => {
    const t = normalize(token);
    const r = normalize(role);
    const m = normalize(method);
    if (t === null) localStorage.removeItem('token');
    else localStorage.setItem('token', t);
    if (r === null) localStorage.removeItem('role');
    else localStorage.setItem('role', r);
    if (m === null) localStorage.removeItem('login_method');
    else localStorage.setItem('login_method', m);
    const next = { token: t, role: r, method: m };
    setAuth(next);
    window.dispatchEvent(new Event('auth:changed'));
  };

  // ----- ログアウト：token/role/login_method を削除して状態を初期化 -----
  const logout = () => {
    // Clear LINE LIFF session IF it was a LINE login
    const liffId = process.env.REACT_APP_LINE_LIFF_ID;
    const wasLineLogin = auth.method === 'line';

    if (liffId && wasLineLogin) {
      import('@line/liff')
        .then(({ default: liff }) => {
          return liff.init({ liffId }).then(() => {
            if (liff.isLoggedIn()) {
              liff.logout();
            }
          });
        })
        .catch(console.error);
    }

    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('login_method');
    // 法人リンクの保留(社員認証途中)もログアウトで破棄(別ユーザーへの引き継ぎ・ピン留め防止)
    try {
      sessionStorage.removeItem('pending_corp_code');
      sessionStorage.removeItem('pending_corp_code_at');
    } catch { /* noop */ }
    setAuth({ token: null, role: null, method: null });
    window.dispatchEvent(new Event('auth:changed'));
    toast.success(i18n.t('auth.toast_logout_success') || 'Logout successful');
  };

  // ----- 厳密な認証判定 -----
  const isAuthenticated = !!auth.token && !isTokenExpired(auth.token);

  const value: Ctx = useMemo(
    () => ({ auth, setAuth, login, logout, isAuthenticated }),
    [auth]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
